import React, { useEffect, useRef, useState } from "react";

import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Table, Tooltip } from "antd";
import moment from "moment";
import { toast } from "react-toastify";

import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import UploadDetailsModal from "./UploadDetailsModal";

import { downloadFileFromS3WithPresigned } from "../../../utils/helpers";
import { convertUtcToLocal } from "../../Common/utils/dateUtils";

import { mlExcelUploadData } from "../../../actions/onboarding";

import { ReactComponent as LoadingDots } from "../../../assets/images/common/LoadingDots.svg";
import { ReactComponent as IncompleteIcon } from "../../../assets/images/common/spec-flag-icons/IncompleteIcon.svg";
import { ReactComponent as InProcessIcon } from "../../../assets/images/common/spec-flag-icons/InProcessIcon.svg";
import { ReactComponent as InSpecIcon } from "../../../assets/images/common/spec-flag-icons/InSpecIcon.svg";
import { ReactComponent as OutOfSpecIcon } from "../../../assets/images/common/spec-flag-icons/OutOfSpecIcon.svg";

import "./UploadedFilesList.css";

const Statuses = {
  success: { color: "var(--color-content-positive)", icon: InSpecIcon, label: "Success" },
  partial: { color: "var(--color-primary)", icon: IncompleteIcon, label: "Partial" },
  pending: { color: "var(--color-content-warning)", icon: InProcessIcon, label: "Pending" },
  failed: { color: "var(--color-content-negative)", icon: OutOfSpecIcon, label: "Failed" },
};

function getFileNameFromPath(path) {
  return (path || "").split("/").pop() || "";
}

const getCategoryLabel = (sampleCategory) => {
  if (sampleCategory === "product") return "Product";
  if (sampleCategory === "environment") return "Environment";
  return "-";
};

export default function UploadedFilesList({ onNext, onClose }) {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingFilePath, setDownloadingFilePath] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);

  const abortControllerRef = useRef();
  const downloadRef = useRef(null);
  const mountedRef = useRef(true);

  async function handleDownloadFile(s3FilePath, fileName) {
    if (!s3FilePath || !fileName) {
      toast.error("Unable to download file");
      return;
    }
    if (downloadRef.current?.filePath === s3FilePath) return;

    if (downloadRef.current?.controller) downloadRef.current.controller.abort();
    const controller = new AbortController();
    downloadRef.current = { controller, filePath: s3FilePath };
    setDownloadingFilePath(s3FilePath);

    try {
      const success = await downloadFileFromS3WithPresigned(
        [s3FilePath],
        "private",
        [fileName],
      );
      if (!mountedRef.current) return;
      if (success) toast.success(`${fileName} downloaded`);
      else toast.error("Unable to download file");
      if (mountedRef.current && downloadRef.current.controller === controller) {
        downloadRef.current = null;
        setDownloadingFilePath(null);
      }
    } catch (error) {
      if (mountedRef.current && downloadRef.current?.controller === controller) {
        downloadRef.current = null;
        setDownloadingFilePath(null);
      }
      toast.error("Unable to download file");
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    setLoading(true);
    mlExcelUploadData(signal).then((response) => {
      setDataSource(response.success ? response.result : []);
      if (!response.success && !response.cancelled) toast.error(response.message);
    }).finally(() => { setLoading(false); });

    return () => {
      mountedRef.current = false;
      const controller = abortControllerRef.current;
      if (controller) controller.abort();
    };
  }, []);

  const columns = [
    {
      title: "Filename",
      dataIndex: "s3_file_path",
      key: "s3_file_path",
      width: "30%",
      render: (filePath) => {
        const fileName = getFileNameFromPath(filePath);
        return (
          <p className="UploadedFilesList__FilenameText" title={fileName}>
            {fileName}
          </p>
        );
      },
      // render: (filePath, record) => {
      //   const fileName = getFileNameFromPath(filePath);
      //   const isDownloading = downloadingFilePath === record.s3_file_path;
      //   return (
      //     <div
      //       role="button"
      //       tabIndex={0}
      //       className={`UploadedFilesList__FilenameRow${isDownloading ? " UploadedFilesList__FilenameRow--disabled" : ""}`}
      //       onClick={() => !isDownloading && handleDownloadFile(record.s3_file_path, fileName)}
      //       onKeyDown={(e) => e.key === "Enter" && !isDownloading && handleDownloadFile(record.s3_file_path, fileName)}
      //       title={fileName}
      //     >
      //       <p className="UploadedFilesList__FilenameText">{fileName}</p>
      //       <div className="UploadedFilesList__FilenameIcon">
      //         {isDownloading ? (
      //           <LoadingDots width={12} height={12} />
      //         ) : (
      //           <DownloadOutlined className="UploadedFilesList__DownloadIcon" aria-hidden />
      //         )}
      //       </div>
      //     </div>
      //   );
      // },
    },
    {
      title: "Category",
      dataIndex: "sample_category",
      key: "sample_category",
      width: "12%",
      render: (sampleCategory) => getCategoryLabel(sampleCategory),
    },
    {
      title: "Requested By",
      dataIndex: "requested_by",
      key: "requested_by",
      width: "28%",
      render: (requestedBy, record) => {
        const requestDate = record.request_date
          ? `${moment(new Date(convertUtcToLocal(record.request_date))).format(
            "DD MMM YYYY | hh:mm a",
          )}`
          : null;
        const cell = <span>{requestedBy}</span>;
        return requestDate ? <Tooltip title={requestDate}>{cell}</Tooltip> : cell;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "20%",
      render: (status) => {
        const color = Statuses[status]?.color;
        const label = Statuses[status]?.label;
        const IconComponent = Statuses[status]?.icon;
        if (!IconComponent) return null;
        return (
          <span
            className={`UploadedFilesList__StatusCell${status === "partial" ? " UploadedFilesList__StatusCell--partial" : ""}`}
            style={{ color }}
          >
            <IconComponent />
            {label}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      render: (_, record) => {
        const fileName = getFileNameFromPath(record.s3_file_path);
        const isDownloading = downloadingFilePath === record.s3_file_path;
        return (
          <div className="UploadedFilesList__ActionsCell">
            <Tooltip title="Download">
              <div
                role="button"
                tabIndex={0}
                className={`UploadedFilesList__FilenameRow${isDownloading ? " UploadedFilesList__FilenameRow--disabled" : ""}`}
                onClick={() => !isDownloading && handleDownloadFile(record.s3_file_path, fileName)}
                onKeyDown={(e) => e.key === "Enter" && !isDownloading && handleDownloadFile(record.s3_file_path, fileName)}
              >
                <div className="UploadedFilesList__FilenameIcon">
                  {isDownloading ? (
                    <LoadingDots width={12} height={12} />
                  ) : (
                    <DownloadOutlined className="UploadedFilesList__DownloadIcon" aria-hidden />
                  )}
                </div>
              </div>
            </Tooltip>
            <Tooltip title={record.status === "pending" ? "Details not available for pending uploads" : "View Upload Details"}>
              <div
                role="button"
                tabIndex={record.status === "pending" ? -1 : 0}
                className={`UploadedFilesList__FilenameRow${record.status === "pending" ? " UploadedFilesList__FilenameRow--disabled" : ""}`}
                onClick={() => record.status !== "pending" && setDetailRecord(record)}
                onKeyDown={(e) => e.key === "Enter" && record.status !== "pending" && setDetailRecord(record)}
              >
                <div className="UploadedFilesList__FilenameIcon">
                  <EyeOutlined className="UploadedFilesList__DownloadIcon" aria-hidden />
                </div>
              </div>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <div className="UploadedFilesList">
      <StyledScrollbar className="UploadedFilesList__TableScrollbar">
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          rowKey={(record) => `${record.s3_file_path}${record.request_date}`}
          rootClassName="UploadedFilesList__Table"
          loading={loading}
          sticky
        />
      </StyledScrollbar>
      {detailRecord && (
        <UploadDetailsModal
          record={detailRecord}
          onClose={() => setDetailRecord(null)}
        />
      )}
      <div className="UploadedFilesList__ButtonContainer">
        <Button
          type="primary"
          ghost
          htmlType="button"
          onClick={onClose}
        >
          Close
        </Button>
        <Button type="primary" htmlType="button" onClick={onNext}>
          Upload Specs
        </Button>
      </div>
    </div>
  );
}
